import React, { useRef, useState, useEffect } from 'react';
import {
  get,
  map,
  isEmpty,
  intersection,
  flatten,
  flow,
  values
} from 'lodash/fp';

import {
  Form,
  Schema,
  FormControl,
  ControlLabel,
  CheckPicker,
  Button,
  ButtonToolbar,
  Icon,
  DateRangePicker,
  Panel
} from 'rsuite';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchReservationTrackingMethodOptions,
  fetchReservationOrderedItemsReport
} from '../../../../redux/Reservation/sagas/routines';
import { getIsFetching } from '../../../../redux/Inventory/selectors/reports/guest-summary';

import {
  getAll as getTrackingMethodsOptions,
  getIsFetching as getIsFetchingTrackingMethodsOptions
} from '../../../../redux/Reservation/selectors/tracking-method-options';
import {
  resetOrderedItemsReport,
  updateOrderedItemsReportSearchQuery
} from '../../../../redux/Reservation/actions/reports/ordered-items';
import {
  InlineHelpBlock,
  InlineFormGroup
} from '../../../widgets/FormElements';
import { DateRangeOptions, GroupByOptions } from './form-options';
// import ReportTemplatesModal from '../../../../containers/ReportTemplatesModal';
import * as ReservationStatus from '../../../../constants/reservation/Status';
import StatusLabels from '../../../../constants/reservation/StatusLabels';
import { toStandardDate } from '../../../../helpers/dates';

const StyledPanel = styled(Panel)`
  > .rs-panel-body {
    padding-bottom: 32px;
  }
`;

const StyledFormGroup = styled(InlineFormGroup)`
  margin-bottom: 0 !important;
`;

const ColumnsCheckPicker = styled(CheckPicker)`
  label! > .sub-menu-item {
    background: red;
  }
`;

const { StringType, ArrayType } = Schema.Types;

const ReservationStatusOptions = [
  {
    value: ReservationStatus.COMPLETED,
    label: StatusLabels[ReservationStatus.COMPLETED]
  },
  {
    value: ReservationStatus.ACTIVE,
    label: StatusLabels[ReservationStatus.ACTIVE]
  },
  {
    value: ReservationStatus.ON_CALL,
    label: StatusLabels[ReservationStatus.ON_CALL]
  },
  {
    value: ReservationStatus.PENDING,
    label: StatusLabels[ReservationStatus.PENDING]
  },
  {
    value: ReservationStatus.REJECTED,
    label: StatusLabels[ReservationStatus.REJECTED]
  },
  {
    value: ReservationStatus.CANCELED,
    label: StatusLabels[ReservationStatus.CANCELED]
  }
];

const groupByValues = map('value', GroupByOptions);
const columnValues = flow(
  values,
  flatten,
  map('value')
)(GroupByOptions);

export default () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  // const [showPresetModal, setShowPresetModal] = useState(false);

  const isFetching = useSelector(getIsFetching);
  const trackingMethodOptions = useSelector(getTrackingMethodsOptions);
  const isLoadingTrackingMethods = useSelector(
    getIsFetchingTrackingMethodsOptions
  );

  const revalidateForm = () => {
    setTimeout(formRef.current.check, 1);
  };

  const setDefaultFormValue = () => {
    setFormValue({
      status: [],
      trackingMethodId: [],
      requestTime: get('0.value', DateRangeOptions)
    });
  };

  useEffect(() => {
    dispatch(fetchReservationTrackingMethodOptions());
  }, []);

  useEffect(
    () => {
      if (!isEmpty(trackingMethodOptions)) {
        setDefaultFormValue();
      }
    },
    [trackingMethodOptions]
  );

  const executeSearch = query => {
    const requestTime = map(toStandardDate, query.requestTime);
    const searchQuery = {
      ...query,
      requestTime,
      groupBy: intersection(query.groupBy, groupByValues),
      columns: intersection(query.groupBy, columnValues)
    };
    dispatch(fetchReservationOrderedItemsReport(searchQuery));
    dispatch(updateOrderedItemsReportSearchQuery(searchQuery));
  };

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFormCheck = error => {
    setFormError(error);
  };

  const handleFormSubmit = () => {
    const noError = formRef.current.check();
    if (noError) {
      executeSearch(formValue);
    }
  };

  const handleFormReset = () => {
    setDefaultFormValue();
    revalidateForm();
    dispatch(resetOrderedItemsReport());
  };

  // const onHidePresetModal = () => {
  //   setShowPresetModal(false);
  // };

  // const handleTogglePresetModal = () => {
  //   const showModal = !showPresetModal;
  //   setShowPresetModal(showModal);
  // };

  // const handleSelectTemplate = ({ file }) => {
  //   onHidePresetModal();
  //   const requestTime = get('requestTime', formValue);
  //   const searchQuery = { ...file, requestTime };
  //   setFormValue(searchQuery);
  //   revalidateForm();

  //   if (!isEmpty(requestTime)) {
  //     executeSearch(searchQuery);
  //   }
  // };

  const disabledAction = isFetching || isLoadingTrackingMethods;

  const formModel = Schema.Model({
    requestTime: ArrayType().isRequired('This field is required'),
    trackingMethodId: ArrayType()
      .isRequired('This field is required')
      .minLength(1, 'Please select at least 1.'),
    status: ArrayType()
      .isRequired('This field is required')
      .minLength(1, 'Please select at least 1.'),
    groupBy: ArrayType().of(StringType())
  });

  return (
    <StyledPanel bordered>
      <Form
        ref={formRef}
        model={formModel}
        formValue={formValue}
        onChange={handleFormChange}
        onCheck={handleFormCheck}
        style={{ overflow: 'visible' }}
      >
        <StyledFormGroup>
          <ControlLabel>
            Select Date From - To*
            <InlineHelpBlock tooltip>
              Select the date range you are reporting for and click
              &quot;OK&quot;
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 230 }}
            placeholder="Select date range"
            name="requestTime"
            accepter={DateRangePicker}
            ranges={DateRangeOptions}
            errorMessage={get('requestTime', formError)}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Tracking Methods*
            <InlineHelpBlock tooltip>
              Select the Tracking Methods you are reporting on.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 180 }}
            placeholder={
              isLoadingTrackingMethods ? 'Loading...' : 'Tracking Methods'
            }
            name="trackingMethodId"
            accepter={CheckPicker}
            data={trackingMethodOptions}
            errorMessage={formError.trackingMethodId}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Reservation Status*
            <InlineHelpBlock tooltip>
              Select the reservation status you are reporting on.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 180 }}
            placeholder="Reservation Status"
            name="status"
            accepter={CheckPicker}
            data={ReservationStatusOptions}
            errorMessage={formError.status}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Columns
            <InlineHelpBlock tooltip>
              Select the type of Columns you want to run.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 170 }}
            placeholder="Columns"
            name="groupBy"
            accepter={ColumnsCheckPicker}
            data={GroupByOptions}
            errorMessage={formError.groupBy}
          />
        </StyledFormGroup>
        <ButtonToolbar style={{ float: 'right', marginTop: 24 }}>
          <Button
            appearance="primary"
            onClick={handleFormSubmit}
            disabled={disabledAction}
          >
            <Icon icon="search" /> Search
          </Button>
          <Button
            appearance="default"
            onClick={handleFormReset}
            disabled={disabledAction}
          >
            <Icon icon="refresh" /> Reset
          </Button>
          {/* <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>View Presets</Tooltip>}
          >
            <Button
              appearance="primary"
              color="green"
              onClick={handleTogglePresetModal}
              disabled={disabledAction}
            >
              <Icon icon="list" /> Presets
            </Button>
          </Whisper> */}
        </ButtonToolbar>
      </Form>
      {/* <ReportTemplatesModal
        show={showPresetModal}
        onHide={onHidePresetModal}
        onSelect={handleSelectTemplate}
        fileType={GUEST_SUMMARY}
      /> */}
    </StyledPanel>
  );
};
